<template>
  <component
    :is="to ? NuxtLink : 'button'"
    :to="to ? to : undefined"
    :type="type ? type : !to ? 'button' : undefined"
    :class="
      variant !== 'unstyled'
        ? [
            'inline-flex items-center justify-center whitespace-nowrap rounded-full text-center font-medium uppercase leading-none no-underline transition-colors duration-300',
            {
              'bg-black text-yellow hover:text-grey-light': variant === 'solid' && !disabled,
              'border border-black bg-transparent text-black hover:bg-black hover:text-yellow':
                variant === 'outline' && !disabled,
              'text-primary hover:bg-primary hover:bg-opacity-5': variant === 'link' && !disabled,
              'cursor-not-allowed': disabled || loading,
              'border-disabled bg-disabled-50 text-disabled': disabled,
              'h-8 min-w-8 gap-1 px-3 text-sm': size === 'sm',
              'h-10 min-w-10 gap-4 px-4': size === 'md',
              'h-12 min-w-12 gap-3 px-6': size === 'lg',
              '!p-0': icon && !label
            }
          ]
        : undefined
    "
    @click="onClick"
  >
    <span v-if="label">
      {{ label }}
    </span>
    <slot v-else />
    <Icon
      v-if="icon"
      class="shrink-0"
      :name="typeof icon === 'string' ? icon : icon.name"
      :size="typeof icon === 'string' ? '20' : icon.size"
      :style="typeof icon === 'object' && icon.style"
    />
  </component>
</template>

<script setup lang="ts">
import { NuxtLink, Icon } from '#components'
import type { RouteLocationRaw } from 'vue-router'
import type { NuxtLinkProps } from 'nuxt/app'

type ButtonVariant = 'solid' | 'outline' | 'link' | 'unstyled'

interface IconProps {
  name: string
  size?: string
  style?: string
}

interface Props {
  disabled?: boolean
  inactive?: boolean
  icon?: IconProps | string
  variant?: ButtonVariant
  label?: string
  loading?: boolean
  to?: RouteLocationRaw | NuxtLinkProps['to']
  prevent?: boolean
  type?: HTMLButtonElement['type']
  size?: 'sm' | 'md' | 'lg'
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  label: undefined,
  to: undefined,
  icon: undefined,
  variant: 'solid',
  loading: false,
  prevent: false,
  type: undefined,
  size: 'md'
})

const emit = defineEmits<{
  (event: 'click', e: MouseEvent): void
}>()

function onClick(event: MouseEvent) {
  if (props.disabled || props.loading || props.prevent) {
    event.preventDefault()
    event.stopPropagation()
    return
  }
  emit('click', event)
}
</script>
